/**
 * ============================================================================
 * СЕРВИС АУТЕНТИФИКАЦИИ
 * ============================================================================
 * Методы для работы с аутентификацией пользователей
 * Интеграция с backend API и useAppStore
 * ============================================================================
 */

import api from './api'
import { useAppStore } from '@/stores/useAppStore'
import authState from '@/utils/authState'
import accountSocket from './accountSocket'

/**
 * Сохранение токенов в localStorage
 */
function saveTokens(tokens) {
	if (!tokens || typeof tokens !== 'object') {
		console.error('❌ [saveTokens] Invalid tokens:', tokens)
		return false
	}

	// Поддержка всех возможных форматов от бэкенда
	const accessToken = tokens.access_token || tokens.accessToken || tokens.token

	const refreshToken = tokens.refresh_token || tokens.refreshToken

	if (!accessToken) {
		console.error('❌ [saveTokens] No access token! Keys:', Object.keys(tokens))
		return false
	}

	localStorage.setItem('access_token', accessToken)

	if (refreshToken) {
		localStorage.setItem('refresh_token', refreshToken)
	}

	accountSocket.updateToken(accessToken)
	window.dispatchEvent(new CustomEvent('auth:login'))

	console.log('✅ Token saved:', accessToken.substring(0, 20) + '...')
	return true
}

/**
 * Очистка токенов из localStorage
 */
function clearTokens() {
	localStorage.removeItem('access_token')
	localStorage.removeItem('refresh_token')
	localStorage.removeItem('login_time')
	accountSocket.disconnect()
}

let authBackgroundRefreshPromise = null

function triggerBackgroundTokenRefresh() {
	const refreshToken = localStorage.getItem('refresh_token')
	if (!refreshToken) {
		return Promise.resolve(false)
	}

	if (authBackgroundRefreshPromise) {
		return authBackgroundRefreshPromise
	}

	authBackgroundRefreshPromise = api
		.post('/auth/refresh', { refreshToken })
		.then(response => {
			if (response?.success && response?.data?.tokens) {
				saveTokens(response.data.tokens)
				return true
			}

			clearTokens()
			return false
		})
		.catch(error => {
			console.warn('[auth] Background refresh failed:', error)
			clearTokens()
			return false
		})
		.finally(() => {
			authBackgroundRefreshPromise = null
		})

	return authBackgroundRefreshPromise
}

/**
 * Получение прав доступа на основе ролей
 */
function getRolePermissions(roles) {
	const permissions = new Set()

	if (roles.includes('admin')) {
		// Администратор имеет полный доступ
		permissions.add('users.view')
		permissions.add('users.create')
		permissions.add('users.edit')
		permissions.add('users.delete')
		permissions.add('courses.view')
		permissions.add('courses.create')
		permissions.add('courses.edit')
		permissions.add('courses.delete')
		permissions.add('lessons.view')
		permissions.add('lessons.create')
		permissions.add('lessons.edit')
		permissions.add('lessons.delete')
		permissions.add('enrollments.view')
		permissions.add('enrollments.create')
		permissions.add('enrollments.edit')
		permissions.add('enrollments.delete')
		permissions.add('reports.view')
		permissions.add('settings.edit')
	}

	if (roles.includes('teacher')) {
		// Преподаватель может управлять курсами и видеть студентов
		permissions.add('courses.view')
		permissions.add('courses.create')
		permissions.add('courses.edit')
		permissions.add('lessons.view')
		permissions.add('lessons.create')
		permissions.add('lessons.edit')
		permissions.add('enrollments.view')
		permissions.add('enrollments.create')
		permissions.add('students.view')
		permissions.add('reports.view')
	}

	if (roles.includes('student')) {
		// Студент может только просматривать курсы и свои записи
		permissions.add('courses.view')
		permissions.add('lessons.view')
		permissions.add('enrollments.view')
		permissions.add('profile.edit')
	}

	return Array.from(permissions)
}

/**
 * Сервис аутентификации
 */
export const authService = {
	/**
	 * Проверка публичных настроек (разрешена ли регистрация)
	 */
	async getPublicSettings() {
		try {
			const response = await api.get('/settings/public')
			return response.data || { allowRegistration: true, maintenanceMode: false }
		} catch {
			// При ошибке возвращаем дефолты
			return { allowRegistration: true, maintenanceMode: false }
		}
	},

	/**
	 * Регистрация нового пользователя
	 * @param {Object} userData - Данные пользователя
	 * @param {string} userData.email - Email
	 * @param {string} userData.password - Пароль
	 * @param {string} userData.full_name - Полное имя
	 * @param {string} userData.invite_token - Токен приглашения (опционально)
	 * @returns {Promise<Object>} Данные пользователя и токены
	 */
	async register({ email, password, full_name, first_name, last_name, middle_name, invite_token }) {
		const appStore = useAppStore()
		try {
			appStore.setLoading(true)

			const payload = { email, password }
			
			// Поддержка обоих форматов: full_name или отдельные поля
			if (full_name) {
				payload.full_name = full_name
			} else if (first_name || last_name) {
				payload.first_name = first_name
				payload.last_name = last_name
				if (middle_name) payload.middle_name = middle_name
			}
			
			if (invite_token) {
				payload.invite_token = invite_token
			}

			const response = await api.post('/auth/register', payload)

			if (response.success) {
				const { user, tokens, requiresEmailVerification } = response.data

				// Если требуется верификация email - не сохраняем токены
				if (requiresEmailVerification) {
					// Не показываем success - он будет в AuthPage
					return response // Возвращаем response для редиректа
				}

				// Сохраняем токены (для преподавателей)
				saveTokens(tokens)

				// Сохраняем флаг первого входа
				if (user.isFirstLogin !== undefined) {
					localStorage.setItem('is_first_login', user.isFirstLogin.toString())
				}

				// Обновляем состояние пользователя
				appStore.setUser(user)
				appStore.persistUser()

				// Устанавливаем права доступа на основе ролей
				appStore.setPermissions(getRolePermissions(user.roles))

				appStore.showSuccess('Регистрация успешно завершена!')

				return response
			}
		} catch (error) {
			const appStore = useAppStore()

			// Определяем понятное сообщение об ошибке
			let errorMessage = 'Ошибка при регистрации'

			if (error.response) {
				const status = error.response.status
				const serverMessage =
					error.response?.data?.error?.message || error.response?.data?.message

				if (status === 409) {
					errorMessage = 'Пользователь с таким email уже существует'
				} else if (status === 400) {
					errorMessage =
						serverMessage ||
						'Некорректные данные. Проверьте правильность заполнения полей'
				} else if (status === 500) {
					errorMessage = 'Ошибка сервера. Попробуйте позже'
				} else if (serverMessage) {
					errorMessage = serverMessage
				}
			} else if (error.request) {
				errorMessage = 'Нет связи с сервером. Проверьте подключение к интернету'
			}

			appStore.showError(errorMessage)
			throw error
		} finally {
			appStore.setLoading(false)
		}
	},

	/**
	 * Вход пользователя
	 * @param {string} email - Email или имя пользователя
	 * @param {string} password - Пароль
	 * @returns {Promise<Object>} Данные пользователя и токены или requiresMfa
	 */
	async login(email, password, { captchaId = null, captchaAnswer = null } = {}) {
		const appStore = useAppStore()

		if (!email || !password) {
			appStore.showWarning('Пожалуйста, заполните все поля')
			throw new Error('Пустые поля')
		}

		try {
			appStore.setLoading(true)

			const trimmedEmail = email.trim()
			
			// 🔍 ОТЛАДКА: логируем email перед отправкой
			console.log('🔍 [authService.login] Email before trim:', email)
			console.log('🔍 [authService.login] Email after trim:', trimmedEmail)
			console.log('🔍 [authService.login] Email length:', trimmedEmail.length)
			console.log('🔍 [authService.login] Email has dot:', trimmedEmail.includes('.'))

			const payload = {
				email: trimmedEmail,
				password,
			}

			if (captchaId) payload.captchaId = captchaId
			if (captchaAnswer) payload.captchaAnswer = captchaAnswer

			const response = await api.post('/auth/login', payload)

			// Проверка на требование МФА
			if (response.data?.requiresMfa || response.requiresMfa) {
				const mfaData = response.data || response
				return {
					requiresMfa: true,
					userId: mfaData.userId,
					email: mfaData.email,
				}
			}

			// Обычный вход без МФА
			let user, tokens

			if (response.data?.user) {
				user = response.data.user
				tokens = response.data.tokens
			} else if (response.user) {
				user = response.user
				tokens = response.tokens
			} else {
				console.error('❌ Unknown response structure:', response)
				throw new Error('Неверный формат ответа сервера')
			}

			if (!tokens) {
				console.error('❌ No tokens in response!')
				throw new Error('Сервер не вернул токены')
			}

			const saved = saveTokens(tokens)
			if (!saved) {
				throw new Error('Не удалось сохранить токен')
			}

			// Сохраняем время входа для проверки 8-часового таймаута
			localStorage.setItem('login_time', Date.now().toString())

			if (user.isFirstLogin !== undefined) {
				localStorage.setItem('is_first_login', user.isFirstLogin.toString())
			}

			appStore.setUser(user)
			appStore.persistUser()
			appStore.setPermissions(getRolePermissions(user.roles || []))
			appStore.showSuccess(`Добро пожаловать, ${user.fullName}!`)

			authState.reset()

			return { user, tokens }
		} catch (error) {
			let errorMessage = 'Ошибка при входе'

			if (error.response?.status === 401) {
				errorMessage = 'Неправильный логин или пароль'
			} else if (
				error.response?.status === 429 &&
				error.response?.data?.error?.code === 'CAPTCHA_REQUIRED'
			) {
				// UI покажет капчу; здесь не показываем "красную" ошибку
				throw error
			} else if (error.message) {
				errorMessage = error.message
			}

			appStore.showError(errorMessage)
			throw error
		} finally {
			appStore.setLoading(false)
		}
	},

	/**
	 * Получение капчи для логина (после превышения порога неудачных попыток)
	 * @param {string} email - Email
	 * @returns {Promise<{captchaId: string, question: string, expiresInSeconds: number}>}
	 */
	async getLoginCaptcha(email) {
		const response = await api.post('/auth/captcha', { email: String(email || '').trim() })
		return response.data || response
	},

	/**
	 * Проверка капчи (без попытки входа)
	 * @param {string} email - Email
	 * @param {string} captchaId - ID капчи
	 * @param {string} captchaAnswer - Ответ на капчу
	 * @returns {Promise<{verified: boolean}>}
	 */
	async verifyCaptcha(email, captchaId, captchaAnswer) {
		const response = await api.post('/auth/verify-captcha', {
			email: String(email || '').trim(),
			captchaId: String(captchaId || '').trim(),
			captchaAnswer: String(captchaAnswer || '').trim(),
		})
		return response.data || response
	},

	/**
	 * Проверка МФА кода при логине
	 * @param {number} userId - ID пользователя
	 * @param {string} code - 6-значный код или резервный код
	 * @returns {Promise<Object>} Данные пользователя и токены
	 */
	async verifyMfaLogin(userId, code) {
		const appStore = useAppStore()

		try {
			appStore.setLoading(true)

			const response = await api.post('/mfa/verify-login', {
				userId,
				code,
			})

			const { user, tokens } = response.data

			if (!tokens) {
				throw new Error('Сервер не вернул токены')
			}

			const saved = saveTokens(tokens)
			if (!saved) {
				throw new Error('Не удалось сохранить токен')
			}

			// Сохраняем время входа для проверки 8-часового таймаута
			localStorage.setItem('login_time', Date.now().toString())

			if (user.isFirstLogin !== undefined) {
				localStorage.setItem('is_first_login', user.isFirstLogin.toString())
			}

			appStore.setUser(user)
			appStore.persistUser()
			appStore.setPermissions(getRolePermissions(user.roles || []))
			appStore.showSuccess(`Добро пожаловать, ${user.fullName}!`)

			authState.reset()

			return { user, tokens }
		} catch (error) {
			let errorMessage = 'Неверный код'

			if (error.response?.data?.message) {
				errorMessage = error.response.data.message
			}

			appStore.showError(errorMessage)
			throw error
		} finally {
			appStore.setLoading(false)
		}
	},

	// В методе создания конференции
	async saveConference(conferenceData) {
		// 🔥 Проверка авторизации перед запросом
		const token = localStorage.getItem('access_token')

		if (!token) {
			this.$toast?.error('Сессия истекла. Войдите снова.')
			this.$router.push('/login')
			return
		}

		try {
			const result = await conferencesService.create(
				this.courseId,
				conferenceData
			)
			this.$toast?.success('Конференция создана')
			return result
		} catch (error) {
			if (error.status === 401) {
				this.$toast?.error('Сессия истекла. Войдите снова.')
				this.$router.push('/login')
			} else {
				this.$toast?.error(error.message || 'Ошибка создания конференции')
			}
			throw error
		}
	},

	/**
	 * Выход пользователя
	 */
	async logout() {
		try {
			const appStore = useAppStore()

			console.log('🔍 [AuthService] Starting logout...')

			// 1. Устанавливаем флаг logout для обхода navigation guards
			authState.startLogout()

			// 2. Отправляем событие для компонентов, которые слушают
			window.dispatchEvent(new CustomEvent('auth:logout', { detail: { reason: 'manual' } }))

			// 3. Небольшая задержка для обработки событий
			await new Promise(resolve => setTimeout(resolve, 50))

			// 4. Отправляем запрос на сервер (опционально)
			try {
				await api.post('/auth/logout')
				console.log('✅ [AuthService] Logout request sent to server')
			} catch (error) {
				// Игнорируем ошибки при выходе
				console.warn('⚠️ [AuthService] Logout request failed:', error)
			}

			// 5. Очищаем локальное состояние
			console.log('🔍 [AuthService] Clearing tokens...')
			clearTokens()

			// 6. Очищаем сохранённое состояние навигации админ-панели
			console.log('🔍 [AuthService] Clearing admin navigation state...')
			this.clearAdminNavigationState()

			console.log('🔍 [AuthService] Clearing user from store...')
			appStore.logout()

			console.log('✅ [AuthService] Logout completed successfully')
			// Уведомление показывается в компонентах, не дублируем здесь

			// 7. Сбрасываем флаг logout после успешного завершения
			// (с небольшой задержкой, чтобы навигация успела завершиться)
			setTimeout(() => {
				authState.finishLogout()
			}, 100)
		} catch (error) {
			console.error('❌ [AuthService] Logout error:', error)
			authState.finishLogout()
			throw error
		}
	},

	/**
	 * Очистка сохранённого состояния навигации админ-панели
	 * Вызывается при logout для предотвращения утечки данных между пользователями
	 */
	clearAdminNavigationState() {
		try {
			// Получаем все ключи из localStorage
			const keys = Object.keys(localStorage)

			// Фильтруем ключи, связанные с навигацией админ-панели
			const navigationKeys = keys.filter(
				key =>
					key.startsWith('admin_navigation_state_') ||
					key.startsWith('admin_sidebar_expanded_')
			)

			// Удаляем найденные ключи
			navigationKeys.forEach(key => {
				localStorage.removeItem(key)
				console.log(`🗑️ [AuthService] Removed navigation state: ${key}`)
			})

			console.log(
				`✅ [AuthService] Cleared ${navigationKeys.length} navigation state entries`
			)
		} catch (error) {
			console.error(
				'❌ [AuthService] Failed to clear admin navigation state:',
				error
			)
			// Не прерываем процесс logout при ошибке очистки
		}
	},

	/**
	 * Получение текущего пользователя
	 * @returns {Promise<Object>} Данные пользователя
	 */
	async getCurrentUser() {
		try {
			const response = await api.get('/auth/me')

			if (response.success) {
				const user = response.data
				const appStore = useAppStore()

				// Обновляем состояние
				appStore.setUser(user)
				appStore.persistUser()
				appStore.setPermissions(getRolePermissions(user.roles))

				return user
			}
		} catch (error) {
			console.error('Get current user error:', error)
			throw error
		}
	},

	/**
	 * Обновление токенов
	 * @returns {Promise<Object>} Новые токены
	 */
	async refreshTokens() {
		try {
			const refreshToken = localStorage.getItem('refresh_token')

			if (!refreshToken) {
				throw new Error('No refresh token available')
			}

			const response = await api.post('/auth/refresh', {
				refreshToken,
			})

			if (response.success) {
				const { tokens } = response.data
				saveTokens(tokens)
				return tokens
			}
		} catch (error) {
			console.error('Refresh tokens error:', error)
			clearTokens()
			throw error
		}
	},

	/**
	 * Проверка аутентификации
	 * @returns {boolean} true если пользователь аутентифицирован
	 */
	isAuthenticated() {
		const accessToken = localStorage.getItem('access_token')
		const refreshToken = localStorage.getItem('refresh_token')

		if (!accessToken) {
			if (refreshToken) {
				triggerBackgroundTokenRefresh()
				return true
			}

			console.log('[isAuthenticated] Checking authentication:', {
				hasToken: false,
				tokenLength: 0,
			})
			return false
		}

		try {
			const tokenParts = accessToken.split('.')
			if (tokenParts.length !== 3) {
				if (refreshToken) {
					triggerBackgroundTokenRefresh()
					return true
				}

				clearTokens()
				return false
			}

			const payload = JSON.parse(atob(tokenParts[1]))
			const expiresAt = Number(payload?.exp) * 1000

			if (!Number.isFinite(expiresAt) || expiresAt <= Date.now()) {
				if (refreshToken) {
					triggerBackgroundTokenRefresh()
					return true
				}

				clearTokens()
				return false
			}
		} catch (error) {
			if (refreshToken) {
				triggerBackgroundTokenRefresh()
				return true
			}

			console.warn('[isAuthenticated] Invalid token, clearing session:', error)
			clearTokens()
			return false
		}

		console.log('[isAuthenticated] Checking authentication:', {
			hasToken: true,
			tokenLength: accessToken.length,
		})
		return true
	},
	/**
	 * Инициализация при загрузке приложения
	 * Проверяет наличие токена и загружает данные пользователя
	 */
	async initialize() {
		try {
			if (this.isAuthenticated()) {
				await this.getCurrentUser()
				return true
			}
			return false
		} catch (error) {
			console.error('Auth initialization error:', error)
			clearTokens()
			return false
		}
	},

	/**
	 * Запрос на восстановление пароля
	 * @param {string} email - Email пользователя
	 * @returns {Promise<Object>}
	 */
	async forgotPassword(email) {
		try {
			const response = await api.post('/auth/forgot-password', { email })
			return response.data
		} catch (error) {
			const errorMessage =
				error.response?.data?.error?.message ||
				error.response?.data?.message ||
				'Ошибка отправки кода'
			throw new Error(errorMessage)
		}
	},

	/**
	 * Проверка кода восстановления пароля
	 * @param {string} email - Email пользователя
	 * @param {string} code - Код подтверждения
	 * @returns {Promise<Object>}
	 */
	async verifyResetCode(email, code) {
		try {
			const response = await api.post('/auth/verify-reset-code', { email, code })
			return response.data
		} catch (error) {
			const errorMessage =
				error.response?.data?.error?.message ||
				error.response?.data?.message ||
				'Неверный код'
			throw new Error(errorMessage)
		}
	},

	/**
	 * Сброс пароля
	 * @param {string} email - Email пользователя
	 * @param {string} code - Код подтверждения
	 * @param {string} password - Новый пароль
	 * @returns {Promise<Object>}
	 */
	async resetPassword(email, code, password) {
		try {
			const response = await api.post('/auth/reset-password', { email, code, password })
			return response.data
		} catch (error) {
			const errorMessage =
				error.response?.data?.error?.message ||
				error.response?.data?.message ||
				'Ошибка сброса пароля'
			throw new Error(errorMessage)
		}
	},
}

export default authService
